import React, { Fragment } from 'react';
import styled from 'styled-components';
import PropTypes, { shape } from 'prop-types';
import AddModuleButton from '../atoms/add-module-button';
import ConfigurationInformations from './configuration_informations';
import ModuleSettings from './module_settings';
import LoadingSpinner from '../atoms/loading';
import DimensionRule from './dimension-rule';
import { front_base_url, configurationModuleType, busExpansionType, rangeProductType, configurationStatuses, m172_ranges, pacdrive_ranges, rule_categories, m580_distributed_ranges, m580_complex_connection_colors, possible_starter_selections, m580_hotstandby_ranges, M580_SAFETY_COPROCESSOR_TYPE, RENDER_M580_ARCHITECTURE_SCHEMA, RACK_PAC_TYPES } from '../constants';
import Configuration from '../models/Configuration';
import Device from '../models/Device'
import I10n from '../features/i10n';
import { appConfiguration } from '../utils/app-starter';
import MsxIcons from '../features/msx-icons';
import ConfigurationActionButton from '../atoms/configuration-action-button';
import DropdownMenu from './dropdown_menu';
import { PRIMARY_COLOR } from '../color-constants';
import BackToList from './button_back_to_list';
import CustomNotification from './customNotification';

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
`;

const InlineRow = styled.div`
  position: relative;
  display: inline-block;
  ${props => props.paddingLeft ? `padding-left: ${props.paddingLeft}px;` : ''}
`;

const ConfigurationContainer = styled(Row)`
  display: flex;
  flex-direction: row;
  @media(max-width: 800px) {
    flex-direction: column;
  }
`;

const GraphicalViewWrapper = styled.div`
  display: inline-block;
  white-space: nowrap;
  flex: 3 1 auto;
  position: relative;
  overflow-y: hidden;
  background-color: #fafafa;
  box-shadow: inset 0 0 15px 1px rgba(0, 0, 0, 0.1);
`;

const GraphicalView = styled.div`
  flex: 1 1 100%;
  display: flex;
  flex-direction: row;
  margin: 0;
  padding: ${props => props.showIsland ? 25 : 55}px 5px 40px 5px;
  overflow-y: hidden;
  overflow-x: auto;
  border-radius: 2px;
`;

const GraphicalViewItem = styled.div`
  position: relative;
  height: 100%;
`;

const ButtonContainer = styled.div`
  display: inline-block;
  vertical-align: top;
  height: 160px;
  margin-left: -2px;
`;

const ButtonWrapper = styled.div`
  height: ${props => props.height ? `${props.height}px` : '100%'};
  ${props => props.marginLeft ? `margin-left: ${props.marginLeft}px` : ''}
`;

const LoadingSpinnerWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  overflow: hidden;
  padding: 10px;
  box-sizing: content-box !important;
  ${props => props.width && `width: ${props.width}px;`}
  ${props => props.height && `height: ${props.height}px;`}
`;

const ModuleItemWrapper = styled(GraphicalViewItem)`
  display: inline-block;
  height: 160px;
`;

const ModuleItem = styled.div`
  display: inline-block;
  height: 160px;
  z-index: 1;
`;

const DeviceImageWrapper = styled.label`
  display: block;
  position: relative;
  cursor: pointer;

  ${props => props.$disabled && `pointer-events: none;`}

  :after {
    content: '';
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    cursor: pointer;
    box-sizing: border-box;

    ${props => props.valid ? `
      border: 2px solid transparent;
      border-radius: 2px;
      ${props.selected ? 'background-color: rgba(66, 180, 230, 0.2);' : ``}
    ` : `
      background: repeating-linear-gradient(
        -55deg,
        rgba(230, 66, 66, 0.2),
        rgba(230, 66, 66, 0.2) 10px,
        rgba(230, 66, 66, 0.7) 10px,
        rgba(230, 66, 66, 0.7) 14px
      );
    `}

    ${props => props.selected && `border-color: ${PRIMARY_COLOR};`}

    ${props => props.hasWarning && `border: 2px solid #E47F00;`}

  }

  :hover {
    :after {
      border-color: ${PRIMARY_COLOR};
      ${props => props.valid ? `
        background-color: rgba(66, 180, 230, 0.2);
      ` : `
        background: repeating-linear-gradient(
          -55deg,
          rgba(66, 180, 230, 0.2),
          rgba(66, 180, 230, 0.2) 10px,
          rgba(230, 66, 66, 0.7) 10px,
          rgba(230, 66, 66, 0.7) 14px
        );
      `}
    }
  }

`;

const RackWrapper = styled.div`
  position: relative;
  display: inline-block;
  height: ${props => `${props.height}px`};
  width: ${props => `${props.width}px`};
`;

const RackImage = styled.img`
  position: absolute;
  display: block;
  height: ${props => `${props.height}px`};
  width: ${props => `${props.width}px`};
  opacity: 0.3;
`;

const DeviceImage = styled.img`
  display: block;
  height: ${props => `${props.height}px`};
  width: ${props => `${props.width}px`};
  font-size: 8px;
  writing-mode: vertical-lr;
  text-orientation: upright;
  text-align: center;
`;

const ConfigurationInformationsContainer = styled.div`
  background-color: white;
  flex: 1 0 300px;
  border: 1px solid #e7e6e6;
  @media (max-width: 800px) {
    flex: 1 0 auto;
  }
`;

const BlockModulesWrapper = styled.div`
  display: inline-block;
  position: absolute;
  right: 0px;
`;

const ModuleWithBusWrapper = styled.div`
  display: inline-block;
`;

const HelpContainer = styled.div`
  position: absolute;
  top : ${props => props.isIsland ? '35px' : '5px'};
  left: 5px;
  display: flex;
  align-items: center;
  cursor: pointer;
  color: ${PRIMARY_COLOR};
`;

const HelpText = styled.span`
  font-size: 10px;
  margin-left: 5px;
`;

const ActionsContainer = styled.div`
  position: absolute;
  top : 5px; right: 5px;
  display: flex;
  align-items: center;
`;

const IoTypeButtonWrapper = styled.div`
  margin-right: 5px;
  font-weight: lighter;
`;

const ModuleAndHintWrapper = styled.div`
  display: inline-block;
  vertical-align: top;
  position: relative;
`;

const TooltipWrapper = styled.div`
  position: absolute;
  z-index: 3;
  top: -10px;
  left: 0; right: 0;
`;

const TooltipText = styled.span`
  position: absolute;
  color: white;
  background-color: #323232;
  top: -20px;
  padding: 5px 10px;
  font-size: 12px;
  border-radius: 2px;
`;

const TooltipTriangle = styled.div`
  position: absolute;
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 10px 5px 0 5px;
  border-color: #323232 transparent transparent transparent;
  margin-left: calc(50% - 5px);
`;

const ModuleActionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  cursor: pointer;
  min-width: 200px;
`;

const ModuleActionRow = styled.div`
  display: flex;
  align-items: center;
  padding: 6px 10px;
  color: ${PRIMARY_COLOR};
  color: var(--se-secondary, ${PRIMARY_COLOR});
  :hover {
    background-color: rgb(237, 237, 237);
  }
`;

const RemoveActionRow = styled(ModuleActionRow)`
  color: #DC0A0A;
`;

const NoActionRow = styled.div`
  display: flex;
  align-items: center;
  padding: 6px 10px;
`;

const ModuleActionText = styled.span`
  font-size: 16px;
  font-weight: 300;
  margin-left: 5px;
`;

const ModulesWithHintWrapper = styled.div`
  display: inline-block;
  position: relative;
  vertical-align: top;
`;

const ViewWrapper = styled.div`
  position: relative;
  display: inline-block;
  ${props => props.isLocalRack ? `margin-left: 20px;` : ''}
`;

const M580LocalHorizontalLineConnector = styled.div`
  position: absolute;
  ${props => props.top != null ? `top: ${props.top}px;` : ''}
  ${props => props.right != null ? `right: ${props.right}px;` : ''}
  ${props => props.left != null ? `left: ${props.left}px;` : ''}
  ${props => props.width != null ? `width: ${props.width}px;` : ''}
  height: 6px;
  background-color: ${props => props.color};
`;

const M580LocalVerticalLineConnector = styled.div`
  position: absolute;
  ${props => props.top != null ? `top: ${props.top}px;` : ''}
  ${props => props.right != null ? `right: ${props.right}px;` : ''}
  ${props => props.left != null ? `left: ${props.left}px;` : ''}

  bottom: ${props => props.bottom != null ? props.bottom : -10}px;
  width: 6px;
  background-color: ${props => props.color};
`;

const EmptySpace = styled.div`
  display: inline-block;
  width: ${props => props.width}px;
  height: 160px;
`;

const RemoveLocalRackWrapper = styled.div`
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  right: 0px;
  top: -15px;
  box-sizing: content-box;
  width: 20px;
  min-width: 20px;
  max-width: 20px;

  color: red;
  cursor: pointer;
  border: 1px solid red;
  ${props => props.disabled ? 'pointer-events: none; opacity: 0.5;' : ''}
`;

const HeaderIslandButtonWrapper = styled.div`
  padding: 5px;
`;

const BackTolListWrapper = styled.div`
  display: flex;
`;

const PacSlotTypeLegendWrapper = styled.div`
  position: absolute;
  bottom: 10px;
  left: 5px;
  display: flex;

  @media(max-width: 800px) {
    display: none;
  }
`;

const PacSlotWrapper = styled.div`
  margin-right: 15px;
`;

const PacSlotAcronym = styled.span`
  color: ${props => props.$color ? props.$color : 'black'};
  font-weight: bold;
`;

const PacSlotLabel = styled.span``;

class PacConfigurationPreview extends React.Component {

  static propTypes = {
    configuration: shape(Configuration),
    openModal: PropTypes.func,
    closeModal: PropTypes.func,
    addModuleAccessory: PropTypes.func,
    addModuleAuxiliary: PropTypes.func,
    addModuleSensor: PropTypes.func,
    setModuleWarranty: PropTypes.func,
    addDevices: PropTypes.func,
    addComModules: PropTypes.func,
    addCartridge: PropTypes.func,
    getAccessories: PropTypes.func,
    getModuleActions: PropTypes.func,
    changeSoftwareVersion: PropTypes.func,
    removeCartridge: PropTypes.func,
    duplicateModule: PropTypes.func,
    removeModule: PropTypes.func,
    replaceSelectedAdditionalDevice: PropTypes.func,
    replaceModule: PropTypes.func,
    updateModule: PropTypes.func,
    insertModuleAtSlot: PropTypes.func,
    insertModulesBefore: PropTypes.func,
    insertModulesAfter: PropTypes.func,
    comModulesCategories: PropTypes.array,
    ioModulesCategories: PropTypes.array,
    ioModulesRemainingCriteria: PropTypes.object,
    comModulesRemainingCriteria: PropTypes.object,
    ioModulesSharedOptions: PropTypes.object,
    comModulesSharedOptions: PropTypes.object,
    cartridgesCategories: PropTypes.array,
    comModulesFailedRules: PropTypes.array,
    ioModulesFailedRules: PropTypes.array,
    cartridgesFailedRules: PropTypes.array,
    ioModulesDevicesFailedRules: PropTypes.object,
    comModulesDevicesFailedRules: PropTypes.object,
    cartridgesDevicesFailedRules: PropTypes.object,
    canAddExtensionRack: PropTypes.bool,
    isLoading: PropTypes.bool,
    moduleActionsLoading: PropTypes.bool,
    accessoriesLoading: PropTypes.bool,
    canReplaceHead: PropTypes.bool,
    onAdjustRequested: PropTypes.func,
    pacdriveKits: PropTypes.object,
    allowSavedModifications: PropTypes.bool,
    showIoTypes: PropTypes.func,
    previewConfiguration: PropTypes.func,
    addLocalRack: PropTypes.func,
    removeLocalRack: PropTypes.func,
    hasPowerSupplies: PropTypes.bool,
    isIsland: PropTypes.bool
  }

  constructor(props) {
    super(props);
    this.state = {
      slotModuleSelected: null,
      typeModuleSelected: null,
      /**
       * Indicate where the selected module is :
       *  - null, the selected module is on the main rack.
       *  - any value, the selected module is on an extended local rack.
       */
      rackModuleSelected: null,
      renderPowerSupplyBuses: false,
      renderDimensions: false,
      showHelp: false,
      selectedModuleCandidateAccessories: {},
      selectedModuleActions: {},
      showWarningNotification: true
    }
    this._isMounted = false;
  }

  componentDidMount() {
    this._isMounted = true;
  }

  componentDidUpdate(prevProps) {
    const prevPowerSupplies = (prevProps.configuration?.comModules || []).map(mod => mod.id);
    const currentPowerSupplies = (this.props.configuration?.comModules || []).map(mod => mod.id);

    // Reset warning flag if power supplies are different
    if (!this.state.showWarningNotification && this._isMounted && JSON.stringify(prevPowerSupplies) !== JSON.stringify(currentPowerSupplies)) {
      this.setState({ showWarningNotification: true });
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  updateStateIfMounted(state) {
    if (this._isMounted) {
      this.setState(state);
    }
  }

  /**
   * Custom modulo function.
   *
   * @param {*} n
   * @param {*} m
   * @returns
   */
  mod(n, m) {
    return ((n % m) + m) % m;
  }

  computeHeadModuleDistance = (islandConfiguration, maxModuleHeight, realRackWidth, realRackHeight, maxRackImageWidth, opticalFiberConnection, idxToCheck) => {
    let rightDistanceTop = 0;
    let rightDistanceBottom = 0;
    let topColor = m580_complex_connection_colors.classic;
    let bottomColor= m580_complex_connection_colors.classic;

    const imageRackHeight = this.computeModuleHeight(realRackHeight, maxModuleHeight);
    const imageRackWidth = this.computeModuleWidth(realRackWidth, realRackHeight, imageRackHeight);

    if (!islandConfiguration.headModule) {
      return {
        rightDistanceTop,
        rightDistanceBottom
      };
    }

    const realHeadModuleHeight = islandConfiguration.headModule ? islandConfiguration.headModule.height : 0;
    const realHeadModuleWidth = islandConfiguration.headModule ? islandConfiguration.headModule.width : 0;

    const headModuleHeight = this.computeModuleHeight(realHeadModuleHeight, maxModuleHeight);
    const headModuleWidth = this.computeModuleWidth(realHeadModuleWidth, realHeadModuleHeight, headModuleHeight);

    if (islandConfiguration.rack) {

      const opticalFiberModuleForCurrent = opticalFiberConnection[idxToCheck];
      const opticalFiberModuleForPrevious = opticalFiberConnection[this.mod(idxToCheck - 1, opticalFiberConnection.length)];
      const opticalFiberModuleForNext = opticalFiberConnection[this.mod(idxToCheck + 1, opticalFiberConnection.length)];

      const emptySpaceWidth = this.computeModuleWidth(32, realRackHeight, imageRackHeight);

      // If connected to previous module via optical fiber
      if (opticalFiberModuleForCurrent && opticalFiberModuleForPrevious) {
        rightDistanceTop += emptySpaceWidth * ((islandConfiguration.rack?.maxIoModules || 0) - opticalFiberModuleForCurrent.slot);
        rightDistanceTop += Math.ceil(emptySpaceWidth / 2.0) - 3;
        topColor = m580_complex_connection_colors.optical_fiber;
      } else {
        rightDistanceTop += emptySpaceWidth * (islandConfiguration.numberOfPacIOModulesSlots());
        rightDistanceTop += Math.ceil(headModuleWidth / 2.0) - 3;
      }

      if (opticalFiberModuleForCurrent && opticalFiberModuleForNext) {
        rightDistanceBottom += emptySpaceWidth * ((islandConfiguration.rack?.maxIoModules || 0) - opticalFiberModuleForCurrent.slot);
        rightDistanceBottom += Math.ceil(emptySpaceWidth / 2.0) - 3;
        bottomColor = m580_complex_connection_colors.optical_fiber;
      } else {
        rightDistanceBottom += emptySpaceWidth * (islandConfiguration.numberOfPacIOModulesSlots());
        rightDistanceBottom += Math.ceil(headModuleWidth / 2.0) - 3;
      }
    }

    return {
      rightDistanceTop,
      rightDistanceBottom,
      imageRackWidth,
      topColor,
      bottomColor
    };
  }

  get selectedModule() {
    return this.getModuleAtSlot(this.state.slotModuleSelected, this.state.typeModuleSelected, this.state.rackModuleSelected);
  }

  getModuleAtSlotForConfiguration = (configuration, slot, moduleType) => {
    let mod;
    switch (moduleType) {
      case configurationModuleType.head_module:
        mod = configuration.headModule;
        break;
      case configurationModuleType.com_module:
        mod = configuration.comModules.find(m => m.slot === slot);
        break;
      case configurationModuleType.io_module:
        mod = configuration.ioModules.find(m => m.slot === slot);
        break;
      default:
        break;
    }

    return new Device(mod);
  }

  getModuleAtSlot = (slot, moduleType, rackLocation) => {
    let configurationToCheck = this.props.configuration;
    if (rackLocation != null) {
      configurationToCheck = this.props.configuration.localRacks[rackLocation - 1];
    }

    return this.getModuleAtSlotForConfiguration(configurationToCheck, slot, moduleType);
  }

  get canTooltipAppears() {
    try {
      return localStorage.getItem('tooltip-head-occurs') == null;
    } catch (error) {
      return true;
    }
  }

  refreshSelectedModuleActions = (newSlotSelected, newSelectedType, rackLocation) => {

    const deviceAtSlot = this.getModuleAtSlot(newSlotSelected, newSelectedType, rackLocation);
    // Don't reload module actions for safety coprocessor
    const isCoprocessor = deviceAtSlot?.productType === M580_SAFETY_COPROCESSOR_TYPE;

    if (newSlotSelected !== null && newSelectedType !== null && !isCoprocessor) {
      this.props.getModuleActions(newSlotSelected, newSelectedType, false, rackLocation)
        .then(result => {
          if (result && !result.cancelled) {
            this.updateStateIfMounted({ selectedModuleActions: result });
          }
        })
        .catch(error => { throw error; });
    }
  }

  updateSelectedModule = (newSlotSelected, newSelectedType, rackLocation) => {
    if (newSlotSelected !== this.state.slotModuleSelected || newSelectedType !== this.state.typeModuleSelected || rackLocation !== this.state.rackModuleSelected) {
      this.updateStateIfMounted({
        slotModuleSelected: newSlotSelected,
        typeModuleSelected: newSelectedType,
        rackModuleSelected: rackLocation,
        selectedModuleCandidateAccessories: {},
        moduleActions: {}
      });
      if (newSlotSelected !== null) {
        const modSelected = this.getModuleAtSlot(newSlotSelected, newSelectedType, rackLocation);
        if (modSelected) {
          this.refreshSelectedModuleActions(newSlotSelected, newSelectedType, rackLocation);
        }
      }
    }
  }

  renderModuleActionsItems = () => {

    const items = [];

    if (configurationModuleType.head_module === this.state.typeModuleSelected) {
      if (this.canReplace(this.selectedModule)) {
        items.push(
          <ModuleActionRow onClick={() => {
            this.handleReplaceRequested(false);
          }}>
            <MsxIcons size="nano" name="replace"></MsxIcons>
            <ModuleActionText>{I10n.get('module_setting.action.replace')}</ModuleActionText>
          </ModuleActionRow>
        );
      }
      if (this.canAdjust(this.selectedModule)) {
        items.push(
          <ModuleActionRow onClick={() => {
            this.props.onAdjustRequested(this.selectedModule);
          }}>
            <MsxIcons size="nano" name="pacdrive-adjust"></MsxIcons>
            <ModuleActionText>{I10n.get('module_setting.action.adjust')}</ModuleActionText>
          </ModuleActionRow>
        );
      }

    } else {
      if (this.canReplace(this.selectedModule)) {
        items.push(
          <ModuleActionRow onClick={() => {
            this.handleReplaceRequested(false);
          }}>
            <MsxIcons size="nano" name="replace"></MsxIcons>
            <ModuleActionText>{I10n.get('module_setting.action.replace')}</ModuleActionText>
          </ModuleActionRow>
        );
      }
      if (this.canRemove(this.selectedModule)) {
        items.push(
          <RemoveActionRow onClick={() => { this.handleRemove(); }}>
            <MsxIcons size="nano" name="delete"></MsxIcons>
            <ModuleActionText>{I10n.get('module_setting.action.remove')}</ModuleActionText>
          </RemoveActionRow>
        );
      }
    }

    if (items.length === 0) {
      items.push(<NoActionRow>{I10n.get('screen.config_editor.no_actions_available')}</NoActionRow>);
    }

    return (<>
      {items.map((item, idx) => <Fragment key={idx}>
        {item}
      </Fragment>)}
    </>);
  }

  renderModuleActions = () => {
    return (
      (this.props.moduleActionsLoading || this.props.isLoading) ?
        <LoadingSpinnerWrapper>
          <LoadingSpinner></LoadingSpinner>
        </LoadingSpinnerWrapper> :
        this.renderModuleActionsItems()
    );
  }

  renderModule = (device, maxModuleHeight, rackLocation, hasWarning = false) => {
    const slot = device.slot;
    const moduleType = device.moduleType;
    const realHeight = device.height ? device.height : 0;
    const realWidth = device.width ? device.width : 0;
    const imageHeight = this.computeModuleHeight(realHeight, maxModuleHeight);
    const imageWidth = this.computeModuleWidth(realWidth, realHeight, imageHeight);
    const isConfFinished = configurationStatuses.finished === this.props.configuration.status;
    const isModuleOnHotstandbyExtentionRack = rackLocation != null && m580_hotstandby_ranges.includes(this.props.configuration.rangeInfo?.rangeName);

    const ModuleRender = (
      <>
        <ModuleItem>
          <DeviceImageWrapper
            htmlFor={`${device.id}_${slot}`} valid={device.validator.valid}
            $disabled={isModuleOnHotstandbyExtentionRack}
            selected={moduleType === this.state.typeModuleSelected && slot === this.state.slotModuleSelected && rackLocation === this.state.rackModuleSelected}
            hasWarning={!this.props.isLoading && !isConfFinished && hasWarning && this.state.showWarningNotification}
            onClick={() => {
              this.updateSelectedModule(slot, moduleType, rackLocation);
              try {
                localStorage.setItem('tooltip-head-occurs', '1');
              } catch (error) {
              }
            }}

            onContextMenu={() => {
              this.updateSelectedModule(slot, moduleType, rackLocation);
              try {
                localStorage.setItem('tooltip-head-occurs', '1');
              } catch (error) {
              }
            }
            }
          >
            <DeviceImage
              src={`${front_base_url}/images/small-devices-images/${device.id}_h320px.png`}
              alt={`${device.id} module front view`}
              onError={(e) => { e.target.onerror = null; e.target.src = device.sidePicture }}
              height={imageHeight}
              width={imageWidth}
            >
            </DeviceImage>
          </DeviceImageWrapper>
        </ModuleItem>
      </>
    );

    return (
      <ModuleItemWrapper key={`${device.id}_${slot}`}>
        {<DropdownMenu
          data-clickable
          disabled={isModuleOnHotstandbyExtentionRack}
          fixedTop={20}
          openOnRightClick={true}
          toggleElement={<>{ModuleRender}</>}
          boundingElement={this.wrapperElement}
          containerElement=
          {
            <ModuleActionsWrapper>
              {this.renderModuleActions(false)}
            </ModuleActionsWrapper>
          }>
        </DropdownMenu>}
      </ModuleItemWrapper>
    );
  };

  renderHeadModule = (configuration, maxModuleHeight, rackLocation) => {
    const device = configuration.headModule;

    return (
      <ModuleAndHintWrapper>
        {this.renderModule(device, maxModuleHeight, rackLocation)}
        {this.canTooltipAppears && <TooltipWrapper>
          <TooltipText>
            {I10n.get('screen.config_editor.preview.tooltip')}
          </TooltipText>
          <TooltipTriangle></TooltipTriangle>
        </TooltipWrapper>}
      </ModuleAndHintWrapper>
    );
  }

  renderPacIoModules = (configuration, maxModuleHeight, modulesDimensionRuleOffset, rack, rackLocation) => {

    let components = [];

    let total = configuration.rack?.maxIoModules || 0;

    for (let i = 0; i < total; i++) {

      const modAtSlot = this.getModuleAtSlotForConfiguration(configuration, (i + 1), configurationModuleType.io_module);

      if (modAtSlot.id.length > 0) {
        components.push(
          <ModuleWithBusWrapper key={i}>
            <InlineRow paddingLeft={0}>
              {this.renderModule(modAtSlot, maxModuleHeight, rackLocation)}
            </InlineRow>
          </ModuleWithBusWrapper>
        );
      } else {
        components.push(
          this.renderAddIOButton(configuration, maxModuleHeight, (i + 1), rack, rackLocation)
        );
      }

    }

    return (<>
      {components.length > 0 && <>
        {this.state.showHelp && <DimensionRule
          text={I10n.get('screen.config_editor.preview.pac.modules')}
          orientation={'horizontal'}
          $right={modulesDimensionRuleOffset}
        ></DimensionRule>}
        {this.state.showHelp && rackLocation == null && <DimensionRule
          text={I10n.get('screen.config_editor.preview.pac.extension')}
          orientation={'horizontal'}
          $width={modulesDimensionRuleOffset}
        ></DimensionRule>}

        {components.map((component, idx) => {
          return <React.Fragment key={idx}>{component}</React.Fragment>
        })
        }</>}
    </>);
  }

  renderPacPowerSupplies = (configuration, maxModuleHeight, rackLocation) => {
    let components = [];

    const showConsumptionWarning = this.hasModuleFilteredDueToConsumption() && (this.props.configuration.localRacks?.length || 0) === 0;

    let total = configuration.rack?.maxPowerSupplies || 0;

    for (let i = 0; i < configuration.comModules.length; i++) {
      components.push(
        <ModuleWithBusWrapper key={i}>
          <InlineRow paddingLeft={0}>
            {this.renderModule(configuration.comModules[i], maxModuleHeight, rackLocation, showConsumptionWarning)}
          </InlineRow>
        </ModuleWithBusWrapper>);
    }

    if (configuration.comModules.length < total) {
      components.push(
        <ModuleAndHintWrapper>
          {this.renderAddPowerSupplyButton(configuration, maxModuleHeight, rackLocation)}
          <TooltipWrapper>
            <TooltipText>
              {I10n.get('screen.config_editor.preview.tooltip_pac_power_supply')}
            </TooltipText>
            <TooltipTriangle></TooltipTriangle>
          </TooltipWrapper>
        </ModuleAndHintWrapper>
      );
    }

    return (<>
      {components.length > 0 && <>
        {this.state.showHelp && <DimensionRule
          text={I10n.get('screen.config_editor.preview.pac.power_supply')}
          orientation={'horizontal'}
        ></DimensionRule>}
        {components.reverse().map((component, idx) => {
          return <React.Fragment key={idx}>{component}</React.Fragment>
        })
        }</>}
    </>);
  }

  renderLocalRackConfigurations = (maxModuleHeight, localRackConfigurations) => {
    return localRackConfigurations.map((rackConfiguration, idx) => {
      return (<Fragment key={idx}>
        {this.renderByBlocks(rackConfiguration, (idx + 1), maxModuleHeight)}
      </Fragment>);
    });
  }

  renderLocalRackWiring = (emptySpaceWidth) => {
    return (
      <>
        <M580LocalVerticalLineConnector top={80} right={(emptySpaceWidth / 2.0) - 3} color={m580_complex_connection_colors.xbus}></M580LocalVerticalLineConnector>
        <M580LocalHorizontalLineConnector top={170} right={(emptySpaceWidth / 2.0) -3} left={-(20 + (emptySpaceWidth / 2.0) + 3)} color={m580_complex_connection_colors.xbus}></M580LocalHorizontalLineConnector>
        <M580LocalVerticalLineConnector top={80} left={-(20 + (emptySpaceWidth / 2.0 ) + 3)} color={m580_complex_connection_colors.xbus}></M580LocalVerticalLineConnector>
      </>
    );
  }

  renderLocalRackWiringForHotstandby = (emptySpaceWidth, numberIoModules, rackWidth, distanceHeadModule) => {
    return (
      <>
        {RENDER_M580_ARCHITECTURE_SCHEMA && <>
          <M580LocalVerticalLineConnector top={90} bottom={-20} right={distanceHeadModule.rightDistanceTop - (emptySpaceWidth / 2.0) + 6} color={distanceHeadModule.topColor}></M580LocalVerticalLineConnector>
          <M580LocalHorizontalLineConnector top={178} right={distanceHeadModule.rightDistanceTop - (emptySpaceWidth / 2.0) + 6} width={rackWidth + 20 + (emptySpaceWidth / 2.0)} color={distanceHeadModule.topColor}></M580LocalHorizontalLineConnector>
          <M580LocalVerticalLineConnector top={90} bottom={-20} right={distanceHeadModule.rightDistanceTop + 6 + rackWidth + 20} color={distanceHeadModule.topColor}></M580LocalVerticalLineConnector>
        </>}

        <M580LocalVerticalLineConnector top={80} right={(numberIoModules * emptySpaceWidth) + ((emptySpaceWidth) - 3)} color={m580_complex_connection_colors.optical_fiber}></M580LocalVerticalLineConnector>
        <M580LocalHorizontalLineConnector top={170} right={(numberIoModules * emptySpaceWidth) + ((emptySpaceWidth) - 3)} left={-(20 + (numberIoModules * emptySpaceWidth) + ((emptySpaceWidth) - 3))} color={m580_complex_connection_colors.optical_fiber}></M580LocalHorizontalLineConnector>
        <M580LocalVerticalLineConnector top={80} left={-(20 + (numberIoModules * emptySpaceWidth) + ((emptySpaceWidth) - 3)) } color={m580_complex_connection_colors.optical_fiber}></M580LocalVerticalLineConnector>
      </>
    );
  }

  renderRemoveLocalRackButton = (rackLocation) => {
    const isSaved = this.props.configuration.status === configurationStatuses.finished;

    return (<RemoveLocalRackWrapper disabled={this.props.isLoading} onClick={() => {
      this.handleDuplicateConfiguration(isSaved, () => {
        this.props.openModal({
          modalType: 'confirmation',
          modalProps: {
            onCancel: () => { this.props.closeModal(); },
            onSubmit: () => {
              this.props.closeModal();
              this.updateSelectedModule(null, null, null);
              this.props.removeLocalRack(rackLocation);
            },
            title: I10n.get('module_setting.remove_extension_rack.modal.title'),
            text: I10n.get('module_setting.remove_extension_rack.modal.body'),
            cancelText: I10n.get('app.action.cancel'),
            submitText: I10n.get('app.action.continue'),
          }
        });
      } , false);
    }}>
      <MsxIcons name="close" size="nano"></MsxIcons>
    </RemoveLocalRackWrapper>);
  }

  renderByBlocks = (configuration, rackLocation, maxModuleHeight) => {
    const isLocalRack = rackLocation != null;
    const blocks = configuration.blocks();

    return (
      blocks.map((block, idx) => {
        const rack = configuration.rack;
        const hasRack = rack && rack.id?.length > 0;
        let width = this.computeModuleWidth(block.width(), block.maxModuleHeight(), 160);

        const realRackHeight = rack?.height ? rack.height : 0;
        const realRackWidth = rack?.width ? rack.width : 0;
        const imageRackHeight = this.computeModuleHeight(realRackHeight, maxModuleHeight);
        const imageRackWidth = this.computeModuleWidth(realRackWidth, realRackHeight, imageRackHeight);

        const headModuleDistance = this.computeHeadModuleDistance(configuration, maxModuleHeight, realRackWidth, realRackHeight, realRackWidth, configuration.opticalFiberConnectionSchema(), 0);

        if (hasRack) {
          width = imageRackWidth;
        }

        const emptySpaceWidth = this.computeModuleWidth(32, realRackHeight, imageRackHeight);

        return (
          <ViewWrapper key={idx} isLocalRack={isLocalRack}>
            <RackWrapper width={width} height={160}>
              {rackLocation != null && !this.state.showHelp && !m580_hotstandby_ranges.includes(configuration.rangeInfo?.rangeName) && this.renderRemoveLocalRackButton(rackLocation)}
              {hasRack && <RackImage width={imageRackWidth} height={imageRackHeight} src={`${front_base_url}/images/small-devices-images/${rack.id}_h320px.png`}></RackImage>}
              <BlockModulesWrapper>
                <ModulesWithHintWrapper>
                  {this.renderPacPowerSupplies(configuration, maxModuleHeight, rackLocation)}
                </ModulesWithHintWrapper>
                {(this.props.isIsland || isLocalRack) && !m580_distributed_ranges.includes(configuration?.rangeInfo?.rangeName) ? <>
                  <ModulesWithHintWrapper>
                    {this.renderPacIoModules(configuration, maxModuleHeight, 0, rack, rackLocation)}
                  </ModulesWithHintWrapper>
                  <ModulesWithHintWrapper>
                    {this.state.showHelp && <DimensionRule
                      text={I10n.get('screen.config_editor.preview.pac.extension')}
                      orientation={'horizontal'}
                    ></DimensionRule>}

                    <ModuleWithBusWrapper key={idx}>
                      <InlineRow paddingLeft={0}>
                        {this.renderHeadModule(configuration, maxModuleHeight, rackLocation)}
                      </InlineRow>
                    </ModuleWithBusWrapper>
                  </ModulesWithHintWrapper>
                </> :
                  <>
                    <ModulesWithHintWrapper>
                      {this.state.showHelp && <DimensionRule
                        text={I10n.get('screen.config_editor.preview.pac.cpu')}
                        orientation={'horizontal'}
                      ></DimensionRule>}

                      <ModuleWithBusWrapper key={idx}>
                        <InlineRow paddingLeft={0}>
                          {this.renderHeadModule(configuration, maxModuleHeight, rackLocation)}
                        </InlineRow>
                      </ModuleWithBusWrapper>

                    </ModulesWithHintWrapper>
                    <ModulesWithHintWrapper>
                      {this.renderPacIoModules(configuration, maxModuleHeight, emptySpaceWidth, rack, rackLocation)}
                    </ModulesWithHintWrapper>
                  </>}
              </BlockModulesWrapper>
            </RackWrapper>
            {isLocalRack && <>
              {m580_hotstandby_ranges.includes(configuration.rangeInfo?.rangeName) ? this.renderLocalRackWiringForHotstandby(emptySpaceWidth, configuration.numberOfPacIOModulesSlots(), imageRackWidth, headModuleDistance) : this.renderLocalRackWiring(emptySpaceWidth)}
            </>}
            {configuration.localRacks && this.renderLocalRackConfigurations(maxModuleHeight, configuration.localRacks)}
            {configuration.localRacks.length < configuration.headModule?.maxExtensionRack && this.renderAddLocalRackButton()}
          </ViewWrapper>
        );
      })
    );
  }

  handleDuplicateConfiguration = (isSaved, callback, promptClearPowerSupply = false) => {
    if (isSaved && !this.props.allowSavedModifications) {

      const modalTitle = promptClearPowerSupply ? 'modal.change_saved_configuration_with_power_supply.title' : 'modal.change_saved_configuration.title';
      const modalBody = promptClearPowerSupply ? 'modal.change_saved_configuration_with_power_supply.body' : 'modal.change_saved_configuration.body';

      this.props.openModal({
        modalType: 'confirmation',
        modalProps: {
          onCancel: () => { this.props.closeModal(); },
          onSubmit: () => {
            this.props.closeModal();
            callback();
          },
          title: I10n.get(modalTitle),
          text: I10n.get(modalBody),
          cancelText: I10n.get('app.action.cancel'),
          submitText: I10n.get('app.action.continue'),
        }
      });
    } else {
      if (this.props.hasPowerSupplies && promptClearPowerSupply) {
        this.props.openModal({
          modalType: 'confirmation',
          modalProps: {
            onCancel: () => { this.props.closeModal(); },
            onSubmit: () => {
              this.props.closeModal();
              callback();
            },
            title: I10n.get('screen.reset_power_supply.modal.title'),
            text: I10n.get('screen.reset_power_supply.modal.body'),
            cancelText: I10n.get('app.action.cancel'),
            submitText: I10n.get('app.action.continue'),
          }
        });
      } else {
        callback();
      }
    }
  }

  renderAddPowerSupplyButton = (configuration, maxModuleHeight, rackLocation) => {
    const isSaved = this.props.configuration.status === configurationStatuses.finished;
    const isDisabled = this.props.isLoading;

    let buttonWidth = 40;

    if (configuration.rack) {
      const realRackHeight = configuration.rack?.height ? configuration.rack.height : 0;
      const imageRackHeight = this.computeModuleHeight(realRackHeight, maxModuleHeight);
      buttonWidth = this.computeModuleWidth(67.6, realRackHeight, imageRackHeight) + 2.0;
    }

    const cmp = (
      <AddModuleButton
        width={buttonWidth}
        title={this.buildFailedRulesTooltip('COM')}
        disabled={isDisabled}
        onClick={e => {
          e.preventDefault();
          this.handleDuplicateConfiguration(isSaved, () => {
            this.props.openModal({
              modalType: 'insert_module',
              modalProps: {
                insertionSlot: 1,
                incrementSlot: false,
                moduleType: configurationModuleType.com_module,
                questionType: "POWER_SUPPLY_PAC",
                baseKey: 'add_pac_power',
                onInsert: (referenceID, quantity) => {
                  this.handleAddComModules(referenceID, quantity, rackLocation);
                },
                selectedLocalRack: rackLocation
              },
            });
          }, true);
        }}
      >+</AddModuleButton>
    );

    const lastDevice = configuration.comModules
      .reduce((previous, current) => current.slot > previous.slot ? current : previous, new Device({}));

    return this.renderAddButton(configuration, cmp, lastDevice, maxModuleHeight, buttonWidth);
  }

  renderPacSlotTypeLegend = () => {
    return (<PacSlotTypeLegendWrapper>
      <PacSlotWrapper>
        <PacSlotAcronym $color={'black'}>X : </PacSlotAcronym>
        <PacSlotLabel>{I10n.get('screen.config_editor.xbus_slot')}</PacSlotLabel>
      </PacSlotWrapper>
      <PacSlotWrapper>
        <PacSlotAcronym $color={PRIMARY_COLOR}>E : </PacSlotAcronym>
        <PacSlotLabel>{I10n.get('screen.config_editor.ethernet_slot')}</PacSlotLabel>
      </PacSlotWrapper>
    </PacSlotTypeLegendWrapper>);
  }

  computeAddIOButtonSubTitle = (rack, slot) => {

    if (!this.state.showHelp) {
      return null;
    }

    let result;
    if (rack?.compatiblePacTypes?.includes(RACK_PAC_TYPES.ETHERNET) && !(rack.xBusOnlySlots || []).includes(slot)) {
      result = {label: 'E', color: PRIMARY_COLOR};
    } else {
      result = {label: 'X', color: 'black'};
    }

    return result;
  }

  renderAddIOButton = (configuration, maxModuleHeight, slotToAdd, rack, rackLocation) => {

     const isPACComplexContext = configuration.starterType === possible_starter_selections.PAC && m580_distributed_ranges.includes(configuration.rangeInfo?.rangeName);
     let total = configuration.rack?.maxIoModules || 0;

    const isSaved = this.props.configuration.status === configurationStatuses.finished;

    const isLastSlot = slotToAdd === total;
    // For PAC simple, only the last slot of the main rack should be disabled.
    const shouldDisableForPacSimple = !isPACComplexContext && !this.props.isIsland && !this.props.canAddExtensionRack;
    // For PAC remote, the last slot must be disabled if not possible to add a local extension rack.
    const shouldDisableForPacComplex = isPACComplexContext && ((configuration.headModule?.maxExtensionRack || 0) === 0 || !this.props.canAddExtensionRack);

    const isDisabled = !configuration.allPacPowerSuppliesFilled() || (isLastSlot && (shouldDisableForPacSimple || shouldDisableForPacComplex));

    let buttonWidth = 40;

    if (configuration.rack) {
      const realRackHeight = configuration.rack?.height ? configuration.rack.height : 0;
      const imageRackHeight = this.computeModuleHeight(realRackHeight, maxModuleHeight);
      buttonWidth = this.computeModuleWidth(32, realRackHeight, imageRackHeight) + 2;
    }

    let cmp = (
      <AddModuleButton
        width={buttonWidth}
        title={this.buildFailedRulesTooltip('IO')}
        subTitleObj={this.computeAddIOButtonSubTitle(rack, slotToAdd)}
        disabled={isDisabled}
        onClick={e => {
          e.preventDefault();
          this.handleDuplicateConfiguration(isSaved, () => {

            this.props.openModal({
              modalType: 'insert_module',
              modalProps: {
                insertionSlot: slotToAdd,
                incrementSlot: false,
                moduleType: configurationModuleType.io_module,
                questionType: this.props.configuration?.rangeInfo?.ioModulesQuestionCategory,
                onInsert: (referenceId, quantity) => {
                  this.props.insertModuleAtSlot(slotToAdd, configurationModuleType.io_module, referenceId, quantity, rackLocation).then(() => {
                    const slot = this.state.slotModuleSelected;
                    const moduleType = this.state.typeModuleSelected;
                    this.refreshSelectedModuleActions(slot, moduleType, this.state.rackModuleSelected);
                  });
                },
                selectedLocalRack: rackLocation,
                showWarning: true
              },
            });
          }, true);
        }}
      >+</AddModuleButton>
    );


    // Don't show hotstandby add butttons
    if (rackLocation != null && m580_hotstandby_ranges.includes(configuration.rangeInfo?.rangeName)) {
      cmp = <EmptySpace width={buttonWidth}></EmptySpace>
    }

    const lastDevice = configuration.ioModules
      .reduce((previous, current) => current.slot > previous.slot ? current : previous, new Device({}));

    return this.renderAddButton(configuration, cmp, lastDevice, maxModuleHeight, buttonWidth);
  }

  /**
   * Render the add button if modules gathering is done.
   */
  renderAddButton = (configuration, addBtnComponent, lastDevice, maxModuleHeight, loadingWidth) => {
    const realHeight = lastDevice.height ? lastDevice.height : 0;
    let buttonHeight = this.computeModuleHeight(realHeight, maxModuleHeight);

    if (configuration.rack) {
      const realRackHeight = configuration.rack?.height ? configuration.rack.height : 0;
      buttonHeight = this.computeModuleHeight(realRackHeight, maxModuleHeight);
    }

    return (
      <ButtonContainer>
        {this.props.isLoading && (
          <LoadingSpinnerWrapper width={loadingWidth - 20} height={buttonHeight}>
            <LoadingSpinner width={'11px'} height={'11px'}></LoadingSpinner>
          </LoadingSpinnerWrapper>
        )}
        {!this.props.isLoading && (
          <ButtonWrapper height={buttonHeight}>{addBtnComponent}</ButtonWrapper>
        )}

      </ButtonContainer>
    );
  }

  renderAddLocalRackButton = () => {
    return (
      <ButtonContainer>
        {this.props.isLoading && (
          <LoadingSpinnerWrapper width={20} height={160}>
            <LoadingSpinner width={'11px'} height={'11px'}></LoadingSpinner>
          </LoadingSpinnerWrapper>
        )}
        {!this.props.isLoading && (
          <ButtonWrapper height={160} marginLeft={20}>
            <AddModuleButton
              width={40}
              title={I10n.get('screen.config_editor.add_local_rack')}
              disabled={this.props.isLoading || !this.props.canAddExtensionRack}
              onClick={e => {
                e.preventDefault();
                const config = this.props.configuration;
                const newLocalRackSlot = this.props.configuration.localRacks.length + 1;
                const isSaved = this.props.configuration.status === configurationStatuses.finished;
                this.handleDuplicateConfiguration(isSaved, () => {
                  this.props.openModal({
                    modalType: 'set-rack',
                    modalProps: {
                      onCancel: () => { this.props.closeModal(); },
                      configurationId: config.id,
                      setRack: (rackId) => {
                        this.props.addLocalRack(rackId).then(() => {
                          this.handleDuplicateConfiguration(isSaved, () => {
                            this.props.openModal({
                              modalType: 'insert_module',
                              modalProps: {
                                insertionSlot: 1,
                                incrementSlot: false,
                                moduleType: configurationModuleType.com_module,
                                baseKey: 'add_pac_power',
                                questionType: "POWER_SUPPLY_PAC",
                                onInsert: (referenceID, quantity) => {
                                  this.handleAddComModules(referenceID, quantity, newLocalRackSlot);
                                },
                                disableCrossModal: true,
                                selectedLocalRack: newLocalRackSlot
                              },
                            });
                          }, true);
                        });
                      },
                      isIsland: true,
                      ruggedization: config.rack?.ruggedization
                    },
                  });
                }, true);
              }}
            >+</AddModuleButton>
          </ButtonWrapper>
        )}
      </ButtonContainer>
    );
  }

  computeModuleHeight = (realHeight, maxModuleHeight) => {
    const heightRatio = Math.ceil(100 * realHeight / maxModuleHeight) / 100;
    return 160 * (heightRatio ? heightRatio : 1);
  }

  computeModuleWidth = (realWidth, realHeight, newHeight) => {
    const aspectRatio = realWidth / realHeight;
    return newHeight * (aspectRatio ? aspectRatio.toFixed(2) : 1);
  }

  buildFailedRulesTooltip = (buttonType) => {
    let failedRulesToDisplay = [];
    let failedRulesString = '';
    switch (buttonType) {
      case 'IO':
        failedRulesToDisplay = this.props.ioModulesFailedRules || [];
        break;
      case 'COM':
        failedRulesToDisplay = this.props.comModulesFailedRules || [];
        break;
      default:
        break;
    }

    failedRulesToDisplay.forEach(rule => {
      failedRulesString += (I10n.get(rule.name, 'data', { full: true }) + '\n');
    });

    return failedRulesString;
  }

  hasCartridges = () => {
    return this.state.typeModuleSelected === configurationModuleType.head_module;
  }

  hasConnectionType = () => {
    return this.selectedModule.relatedConnectionDevices?.length > 0;
  }

  hasFuse = () => {
    return Object.values(this.selectedModule.relatedFuseDevices || {}).length > 0;
  }

  hasSlottedAdditionalDevices = () => {
    return this.selectedModule.slottedAdditionalDevices?.length > 0;
  }

  canHandleCartridgeActions = () => {
    return !this.props.isLoading && this.hasCartridges();
  }

  canShowScreenAccessories = (device) => {
    return this.state.typeModuleSelected === configurationModuleType.head_module && m172_ranges.includes(device.rangeOfProduct);
  }

  canShowSparePartAccessories = (device) => {
    return this.state.typeModuleSelected === configurationModuleType.head_module && pacdrive_ranges.includes(device.rangeOfProduct);
  }

  canDuplicate = (device) => {
    return false;
  }

  canRemovePacExtensionModule = (device) => {
    const isPACComplexContext = this.props.configuration.starterType === possible_starter_selections.PAC && m580_distributed_ranges.includes(this.props.configuration.rangeInfo?.rangeName);

    if (isPACComplexContext) {
      return (!device.isPacExtensionModule() || ((this.props.configuration?.localRacks?.length || 0) === 0));
    }

    return (!device.isPacExtensionModule() || ((this.props.configuration?.localRacks?.length || 0) === 0 && (this.props.configuration?.ioIslands?.length || 0) === 0));
  }

  canRemove = (device) => {
    return !this.props.isLoading
      && !this.props.moduleActionsLoading
      && device.productType !== M580_SAFETY_COPROCESSOR_TYPE
      && (this.state.typeModuleSelected !== configurationModuleType.head_module || this.state.rackModuleSelected != null)
      && this.state.typeModuleSelected !== configurationModuleType.com_module
      && this.canRemovePacExtensionModule(device);
  }

  canInsertLeft = (device) => {
    return false;
  }

  canInsertRight = (device) => {
    return false;
  }

  canReplace = (device) => {
    return (!this.props.isLoading
      && !this.props.moduleActionsLoading
      && device.productType !== M580_SAFETY_COPROCESSOR_TYPE
      && ((device.busExpansionType === busExpansionType.none
        && this.state.selectedModuleActions
        && this.state.selectedModuleActions.canReplace === true)
        || (device.moduleType === configurationModuleType.head_module && this.props.canReplaceHead && this.state.rackModuleSelected == null))
    );
  }

  canAdjust = (device) => {
    return false;
  }

  canShowSoftwareVersion = (device) => {
    return device.moduleType === configurationModuleType.head_module
      && this.props.configuration.rangeInfo.productType === rangeProductType.plc
      && device?.possibleSoftwareVersions?.length > 0;
  }

  handleAddComModules = (referenceID, quantity, rackLocation) => {
    this.props.addComModules(referenceID, quantity, rackLocation).then(() => {
      const slot = this.state.slotModuleSelected;
      const moduleType = this.state.typeModuleSelected;
      this.refreshSelectedModuleActions(slot, moduleType, this.state.rackModuleSelected);
    });
  }

  formatConnectedIslandsText = (connectedIslands) => {
    let text = '\n\n';
    connectedIslands.forEach(islandWithProtocol => {
      text += `${islandWithProtocol.islandName} (${islandWithProtocol.protocolName})\n`;
    });
    return text;
  }

  islandsConnectedToModule = (module) => {
    const islandsWithProtocols = [];
    const configurationPLC = this.props.configuration.headModule;
    module.masterProtocols.forEach((protocol) => {
      const protocolName = protocol.name;
      const islandsConnectedWithProtocol =
        this.props.configuration
          .getIslandsConnectedWithProtocol(protocol.name);

      islandsConnectedWithProtocol.forEach(island => {
        if (configurationPLC.masterProtocols.filter(islandProtocol => islandProtocol.name !== protocolName).length > 0) {
          islandsWithProtocols.push({ islandName: island.getName(), protocolName });
        }
      });
    });

    return islandsWithProtocols;
  }

  removeModule = () => {
    const islandsConnectedToModule =
      this.islandsConnectedToModule(this.selectedModule);
    if (islandsConnectedToModule.length > 0) {
      this.props.openModal({
        modalType: 'information',
        modalProps: {
          onButtonClick: () => { this.props.closeModal(); },
          title: '',
          text: I10n.getAndReplace('modal.error.island_attached_to_device.body',
            this.formatConnectedIslandsText(islandsConnectedToModule)),
          buttonText: I10n.get('app.action.close'),
        }
      });
    } else {
      let modalBody = I10n.get('module_setting.remove.modal.body');
      if (this.selectedModule.busExpansionType !== busExpansionType.none) {
        modalBody = I10n.get('module_setting.remove.modal.body_multiple');
      } else if (this.selectedModule.isPowerSupplier()) {
        modalBody = I10n.get('module_setting.remove.modal.body_power_supply');
      }
      this.props.openModal({
        modalType: 'confirmation',
        modalProps: {
          onCancel: () => { this.props.closeModal(); },
          onSubmit: () => {
            this.props.removeModule(this.state.slotModuleSelected, this.state.typeModuleSelected, false, this.state.rackModuleSelected);
            this.updateSelectedModule(null, null, null);
            this.props.closeModal();
          },
          title: I10n.get('module_setting.remove.modal.title'),
          text: modalBody,
          cancelText: I10n.get('app.action.cancel'),
          submitText: I10n.get('app.action.confirm'),
        }
      });
    }
  }

  handleRemove = () => {
    const isSaved = this.props.configuration.status === configurationStatuses.finished;
    this.handleDuplicateConfiguration(isSaved, () => {
      this.removeModule();
    }, true);
  }

  handleRemoveCartridgeRequested = (slot, cartridge) => {
  }

  _changeSoftwareVersion = (newSoftwareVersion) => {
    this.props.changeSoftwareVersion(newSoftwareVersion);
  }

  handleSoftwareVersionChange = (newSoftwareVersion) => {
    const isSaved = this.props.configuration.status === configurationStatuses.finished;
    this.handleDuplicateConfiguration(isSaved, () => {
      this._changeSoftwareVersion(newSoftwareVersion);
    }, false);
  }

  replaceModule = (closeModuleSettings) => {
    const moduleType = this.state.typeModuleSelected;
    const islandsConnectedToModule =
      this.islandsConnectedToModule(this.selectedModule);
    if (islandsConnectedToModule.length > 0 && moduleType !== configurationModuleType.head_module) {
      this.props.openModal({
        modalType: 'information',
        modalProps: {
          onButtonClick: () => { this.props.closeModal(); },
          title: '',
          text: I10n.getAndReplace('modal.error.island_attached_to_device.body',
            this.formatConnectedIslandsText(islandsConnectedToModule)),
          buttonText: I10n.get('app.action.close'),
        }
      });
    } else {
      if (moduleType === configurationModuleType.head_module) {
        this.props.openModal({
          modalType: 'replace_head',
          modalProps: {
            selectedHead: this.selectedModule,
            onReplaceHead: (moduleId) => { this.handleReplace(moduleId, closeModuleSettings) },
          },
        });

      } else {
        this.props.openModal({
          modalType: 'replace_module',
          modalProps: {
            replacementSlot: this.state.slotModuleSelected,
            moduleType: this.state.typeModuleSelected,
            questionType: this.modaleReplaceQuestionType,
            onReplace: (moduleId) => { this.handleReplace(moduleId, closeModuleSettings) },
            baseKey: configurationModuleType.com_module === this.state.typeModuleSelected ? 'add_pac_power' : null,
            selectedLocalRack: this.state.rackModuleSelected
          },
        });
      }
    }
  }

  handleReplaceRequested = (closeModuleSettings = false) => {
    const isSaved = this.props.configuration.status === configurationStatuses.finished;
    this.handleDuplicateConfiguration(isSaved, () => {
      this.replaceModule(closeModuleSettings);
    }, true);
  }

  handleReplace = (referenceID, closeModuleSettings = false) => {
    const slot = this.state.slotModuleSelected;
    const moduleType = this.state.typeModuleSelected;
    const rackLocation = this.state.rackModuleSelected;

    return this.props.replaceModule(slot, moduleType, referenceID, false, rackLocation).then((success) => {
      if (closeModuleSettings) {
        this.updateSelectedModule(null, null, null);
      } else {
        this.refreshSelectedModuleActions(slot, moduleType, rackLocation);
      }
    });
  }

  handleUpdate = (module) => {
    const slot = this.state.slotModuleSelected;
    const moduleType = this.state.typeModuleSelected;

    const isSaved = this.props.configuration.status === configurationStatuses.finished;
    this.handleDuplicateConfiguration(isSaved, () => {
      this.props.updateModule(slot, moduleType, module);
    }, true)
  }

  changeSlottedAdditionalDevice = (layer, selectedDevice) => {
    const slot = this.state.slotModuleSelected;
    const moduleType = this.state.typeModuleSelected;
    const rackLocation = this.state.rackModuleSelected;

    this.props.replaceSelectedAdditionalDevice(slot, moduleType, layer, selectedDevice, rackLocation);
  }

  handleSlottedAdditionalDeviceChange = (layer, selectedDevice) => {
    const isSaved = this.props.configuration.status === configurationStatuses.finished;
    this.handleDuplicateConfiguration(isSaved, () => {
      this.changeSlottedAdditionalDevice(layer, selectedDevice);
    }, true);
  }

  get modaleInsertLeftQuestionType() {
    const moduleType = this.state.typeModuleSelected;
    return moduleType === configurationModuleType.com_module ? 'POWER_SUPPLY_PAC' : this.props.configuration?.rangeInfo?.ioModulesQuestionCategory;
  }


  get modaleReplaceQuestionType() {
    const moduleType = this.state.typeModuleSelected;
    if (moduleType === configurationModuleType.io_module) {
      return this.props.configuration?.rangeInfo?.ioModulesQuestionCategory;
    } else if (moduleType === configurationModuleType.com_module) {
      return 'POWER_SUPPLY_PAC';
    }

    return '';
  }

  onInsertModuleLeftRequested = (closeModuleSettings) => {
  }

  onInsertModuleRightRequested = (closeModuleSettings) => {
  }

  setWrapperRef = element => {
    this.wrapperElement = element;
  };

  /**
   * @returns true if there is one module which have been filtered due to consumption only.
   */
  hasModuleFilteredDueToConsumption = () => {

    let hasModuleFiltered = false;
    Object.keys(this.props.ioModulesDevicesFailedRules).forEach(deviceKey => {
      const rules = this.props.ioModulesDevicesFailedRules[deviceKey] || [];
      const allConstraintGroups = rules.map(rule => rule.constraintGroup);
      const distinctGroups = allConstraintGroups.filter((x, i, a) => a.indexOf(x) === i);
      if (distinctGroups.length === 1 && distinctGroups[0] === rule_categories.consumption) {
        hasModuleFiltered = true;
      }
    });

    return hasModuleFiltered;
  }

  /**
   * Check if the configuration has error due to consumption (24V sensors consumption ommited here).
   *
   * @returns true if has error due to consumption
   */
  hasErrorDueToConsumption = () => {
    let hasError = false;
    const consumptions = [this.props.configuration.pacConsumptionBlock?.consumption3Dot3V, this.props.configuration.pacConsumptionBlock?.consumption24V, (this.props.configuration.pacConsumptionBlock?.consumption3Dot3V / 1000.0 * 3.3) + (this.props.configuration.pacConsumptionBlock?.consumption24V / 1000.0 * 24.0), (this.props.configuration.pacConsumptionBlock?.consumption3Dot3V / 1000.0 * 3.3) + (this.props.configuration.pacConsumptionBlock?.consumption24V / 1000.0 * 24.0) + (this.props.configuration.pacConsumptionBlock?.consumption24VSensors / 1000.0 * 24.0)];
    const maxConsumptions = [this.props.configuration.pacConsumptionBlock?.maxConsumption3Dot3V, this.props.configuration.pacConsumptionBlock?.maxConsumption24V, this.props.configuration.pacConsumptionBlock?.maxComsumption3Dot3and24V, this.props.configuration.pacConsumptionBlock?.maxGlobalConsumption];

    for (let i = 0; i < consumptions.length && !hasError; i++) {

      if (consumptions[i] > maxConsumptions[i]) {
        hasError = true
      }
    }

    return hasError;
  }

  renderNotification = () => {
    const allPacPowerSuppliesFilled = this.props.configuration.allPacPowerSuppliesFilled();
    const isConfFinished = configurationStatuses.finished === this.props.configuration.status;

    if (!this.props.isLoading && allPacPowerSuppliesFilled && this.hasErrorDueToConsumption() && !isConfFinished && this.state.showWarningNotification) {
      return (
        <CustomNotification
          type={'warning'}
          text={I10n.get('configuration.general.consumption.error.pac')}
          hideNotification={() => {
            this.setState({ showWarningNotification: false});
          }}
        />
      );

    }

    return (<></>);
  }

  render = () => {

    const config = this.props.configuration;
    const maxModuleHeight = config.maxModuleHeight();
    const selectedModule = this.selectedModule;
    const hasSelectedModule = !!selectedModule && this.state.slotModuleSelected !== null;

    return (
      <Container>
        {this.renderNotification()}
        <ConfigurationContainer>
          <GraphicalViewWrapper>
            <HelpContainer isIsland={this.props.isIsland} onClick={() => { this.setState({ showHelp: !this.state.showHelp }); }}>
              {this.state.showHelp ? <MsxIcons name="eye-hide" size="nano"></MsxIcons> : <MsxIcons name="eye-show" size="nano"></MsxIcons>}
              <HelpText>{I10n.get(this.state.showHelp ? 'screen.config_editor.preview.hide_help.button' : 'screen.config_editor.preview.show_help.button')}</HelpText>
            </HelpContainer>
            {this.state.showHelp && this.renderPacSlotTypeLegend()}
            {this.props.isIsland &&
            <BackTolListWrapper>
              <HeaderIslandButtonWrapper>
              <BackToList
                unselectIsland={this.props.unselectIsland}
              />
              </HeaderIslandButtonWrapper>
            </BackTolListWrapper>
            }
            <ActionsContainer>
              {this.props.redboxes && this.props.redboxes.length > 0 && <IoTypeButtonWrapper>
                <ConfigurationActionButton
                  title={I10n.get('screen.config_editor.manage_redboxes')}
                  width={32}
                  height={32}
                  onClick={() => {
                    this.props.openModal({
                      modalType: 'manage-redboxes',
                      modalProps: {}
                    });
                  }}>
                  <MsxIcons name="settings" size="xsmall"></MsxIcons>
                </ConfigurationActionButton>
              </IoTypeButtonWrapper>}
              <IoTypeButtonWrapper>
                <ConfigurationActionButton title={I10n.get('screen.config_editor.preview.io_types')} width={32} height={32} onClick={() => {
                  this.props.showIoTypes();
                }}>{I10n.get('screen.config_editor.preview.io_types.button')}</ConfigurationActionButton>
              </IoTypeButtonWrapper>
              <ConfigurationActionButton title={I10n.get('screen.config_editor.preview')} width={32} height={32} onClick={() => {
                this.props.previewConfiguration();
              }}><MsxIcons name="expand" size="nano"></MsxIcons></ConfigurationActionButton>
            </ActionsContainer>
            <GraphicalView ref={this.setWrapperRef} showIsland={this.props.isIsland}>
              {this.renderByBlocks(this.props.configuration, null, maxModuleHeight)}
            </GraphicalView>
          </GraphicalViewWrapper>
            <ConfigurationInformationsContainer>
              <ConfigurationInformations
                configuration={config}
                onConsumptionDisplayChange={(showBuses) => {
                  this.setState({ renderPowerSupplyBuses: showBuses });
                }}
                onRenderDimensionsChange={(showDimensions) => {
                  this.setState({ renderDimensions: showDimensions });
                }}
              >
              </ConfigurationInformations>
            </ConfigurationInformationsContainer>
        </ConfigurationContainer>
        {hasSelectedModule &&
          <ModuleSettings
            module={selectedModule}
            rangeInfo={config.rangeInfo}
            cartridges={config.cartridges}
            cartridgeMaxQuantity={config.headModule.maxCartridges}
            hasCartridges={this.hasCartridges}
            hasConnectionType={this.hasConnectionType}
            hasFuse={this.hasFuse}
            hasSlottedAdditionalDevices={this.hasSlottedAdditionalDevices}
            canHandleCartridgeActions={this.canHandleCartridgeActions}
            canShowScreenAccessories={this.canShowScreenAccessories}
            canShowSparePartAccessories={this.canShowSparePartAccessories}
            onAddCartridgeRequested={(slot) => {

            }}
            onRemoveCartridgeRequested={this.handleRemoveCartridgeRequested}
            onReplaceModuleRequested={this.handleReplaceRequested}
            onAdjustRequested={this.props.onAdjustRequested}
            onInsertModuleLeftRequested={this.onInsertModuleLeftRequested}
            onInsertModuleRightRequested={this.onInsertModuleRightRequested}
            canDuplicate={this.canDuplicate}
            onDuplicate={this.handleDuplicate}
            canRemove={this.canRemove}
            canAdjust={this.canAdjust}
            canInsertLeft={this.canInsertLeft}
            canInsertRight={this.canInsertRight}
            canReplace={this.canReplace}
            onReplace={this.handleReplace}
            onUpdate={this.handleUpdate}
            onSlottedAdditionalDeviceChange={this.handleSlottedAdditionalDeviceChange}
            canShowSoftwareVersion={this.canShowSoftwareVersion}
            onSoftwareVersionChange={this.handleSoftwareVersionChange}
            onRemove={this.handleRemove}
            onClose={() => {
              this.updateSelectedModule(null, null, null);
            }}
            isLoading={this.props.isLoading}
            moduleActionsLoading={this.props.moduleActionsLoading}
            selectedModuleCandidateAccessories={this.state.selectedModuleCandidateAccessories}
            preferredProductReference={appConfiguration.preferredProductReference}
            accessoriesLoading={this.props.accessoriesLoading}
            starterType={this.props.configuration.starterType}
          >
          </ModuleSettings>
        }
      </Container>
    );
  }
}

export default PacConfigurationPreview;
